import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Form, Button } from "react-bootstrap";
import isEqual from 'lodash/isEqual';
import LoadingButton from "../../../../components/ui/LoadingButton";
import styles from "./MeetingAttendanceModal.module.scss";
import { AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE, ALERT_TYPE } from "../../../../constants";
import {
    addAttendanceLogs,
    updateAttendancePostStatus
} from "../../../../ducks/advisoryGroup";
import messages from "../../../../utils/helper/messages";
import { AlertContext } from "../../../../context/AlertContext";

const MeetingAttendanceModal = ({ show, onCancel, data, info }) => {
    const dispatch = useDispatch();
    const { showAlert } = useContext(AlertContext);
    const [loading, setLoading] = useState(false);
    const [checkboxes, setCheckboxes] = useState({});
    const [initialCheckboxes, setInitialCheckboxes] = useState({});

    const initializeCheckboxes = () => {
        let checks = {};
        data?.forEach((attendee) => {
            checks[attendee.id] = attendee.meetingParticipation;
        });
        setCheckboxes(checks);
        setInitialCheckboxes(checks);
    }

    const handleInputChange = (id, meetingParticipation) => {
        setCheckboxes((prevCheckboxes) => ({
            ...prevCheckboxes,
            [id]: meetingParticipation,
        }));
    }

    const handleSuccess = () => {
        setLoading(false);
        showAlert(messages.success.updateMeetingAttendancePostStatus, ALERT_TYPE.SUCCESS);
        onCancel();
    }

    const handleError = (error) => {
        setLoading(false);
        showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);

        let statusPostsUpdate = [];
        let statusPostsCreate = [];

        for (let item in checkboxes) {
            if (checkboxes[item] !== initialCheckboxes[item]) {
                let recordToUpdate = data?.find(rec => {
                    return rec.id === parseInt(item) && rec.attendanceLogId !== null
                });

                let recordToCreate = data?.find(rec => {
                    return rec.id === parseInt(item) && rec.attendanceLogId === null
                });

                if (recordToUpdate) {
                    statusPostsUpdate.push({
                        attendanceLogId: recordToUpdate.attendanceLogId,
                        statusPost: checkboxes[item]
                    });
                }

                if (recordToCreate) {
                    statusPostsCreate.push({
                        ...info,
                        agMemberId: recordToCreate.id,
                        statusPost: checkboxes[item],
                        status: recordToCreate.preMeetingResponse,
                        locationId: recordToCreate.locationId,
                        accommodationsRequested: recordToCreate.accommodationsRequested,
                        careTaker1: null,
                        careTaker2: null,
                        transportation: null,
                    });
                }
            }
        }

        if (statusPostsUpdate.length) {
            dispatch(updateAttendancePostStatus(statusPostsUpdate, handleSuccess));
        }

        if (statusPostsCreate.length) {
            dispatch(addAttendanceLogs(statusPostsCreate, handleSuccess, handleError));
        }
    }

    const isDataUpdated = () => {
        return !(isEqual(checkboxes, initialCheckboxes))
    }

    useEffect(() => {
        data && initializeCheckboxes();
    }, [data]);

    return (
        <Modal
            show={show}
            fullscreen={false}
            backdrop="static"
            onHide={onCancel}
            size="md"
            dialogClassName={styles.modalContainer}>
            <Modal.Header closeButton>
                <Modal.Title>Meeting Attendance</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2">
                    <Form noValidate onSubmit={handleSubmit}>
                        <div className="text-end mb-4">
                            <Button variant="outline-primary" type="button" className="me-2" onClick={onCancel}>Cancel</Button>
                            <LoadingButton type="submit" loading={loading} disabled={loading || !isDataUpdated()}>Submit</LoadingButton>
                        </div>
                        <div className="d-flex mb-3">
                            <div className="flex-grow-1 fw-bold pe-3">Member</div>
                            <div className={`flex-shrink-0 fw-bold pe-2 text-center ${styles.colAttended}`}>Attended</div>
                            <div className={`flex-shrink-0 fw-bold pe-2 text-center ${styles.colCancelled}`}>Cancelled</div>
                            <div className={`flex-shrink-0 fw-bold pe-2 text-center ${styles.colDeclined}`}>Declined</div>
                            <div className={`flex-shrink-0 fw-bold pe-2 text-center ${styles.colNoShow}`}>No Show</div>
                            <div className={`flex-shrink-0 fw-bold text-center ${styles.colFutureMeeting}`}>Unplanned</div>
                        </div>
                        <div>
                            {
                                data?.map((item) => (
                                    <div key={item.id} className="d-flex mb-1 py-1 rounded-2 hover-highlight">
                                        <div className="flex-grow-1 ps-2 pe-3">{item.name}</div>
                                        <div className={`flex-shrink-0 pe-2 text-center ${styles.colAttended}`}>
                                            {
                                                <Form.Check
                                                    type="checkbox"
                                                    name={`att_${item.id}`}
                                                    value={item.meetingParticipation}
                                                    id={`att_${item.id}`}
                                                    checked={checkboxes[item.id] === AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Attended || false}
                                                    onChange={() => handleInputChange(item.id, AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Attended)}
                                                />
                                            }
                                        </div>
                                        <div className={`flex-shrink-0 pe-2 text-center ${styles.colCancelled}`}>
                                            {
                                                <Form.Check
                                                    type="checkbox"
                                                    name={`cnl_${item.id}`}
                                                    value={item.meetingParticipation}
                                                    id={`cnl_${item.id}`}
                                                    checked={checkboxes[item.id] === AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Cancelled || false}
                                                    onChange={() => handleInputChange(item.id, AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Cancelled)}
                                                />
                                            }
                                        </div>
                                        <div className={`flex-shrink-0 pe-2 text-center ${styles.colDeclined}`}>
                                            {
                                                <Form.Check
                                                    type="checkbox"
                                                    name={`dcl_${item.id}`}
                                                    value={item.meetingParticipation}
                                                    id={`dcl_${item.id}`}
                                                    checked={checkboxes[item.id] === AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Declined || false}
                                                    onChange={() => handleInputChange(item.id, AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Declined)}
                                                />
                                            }
                                        </div>
                                        <div className={`flex-shrink-0 pe-2 text-center ${styles.colNoShow}`}>
                                            {
                                                <Form.Check
                                                    type="checkbox"
                                                    name={`nsh_${item.id}`}
                                                    value={item.meetingParticipation}
                                                    id={`nsh_${item.id}`}
                                                    checked={checkboxes[item.id] === AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.NoShow || false}
                                                    onChange={() => handleInputChange(item.id, AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.NoShow)}
                                                />
                                            }
                                        </div>
                                        <div className={`flex-shrink-0 text-center ${styles.colFutureMeeting}`}>
                                            {
                                                <Form.Check
                                                    type="checkbox"
                                                    name={`nsh_${item.id}`}
                                                    value={item.meetingParticipation}
                                                    id={`nsh_${item.id}`}
                                                    checked={checkboxes[item.id] === AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Unplanned || false}
                                                    onChange={() => handleInputChange(item.id, AG_MEETING_ATTENDANCE_STATUS_POST_INVERSE.Unplanned)}
                                                />
                                            }
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </Form>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default MeetingAttendanceModal;
